import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { CLESSON_MODES } from '@yandex-int/k-common/constants';
import { CLessonInCourse, LESSON_STATUS } from '@yandex-int/k-common/typings';
import { SimpleCallback, Text } from 'platform-components';
import { UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/src/components/constants';
import React, { Ref } from 'react';
import { accessibleClick } from '@edu-frontend/modules/src/shared/lib/a11y';

import { LessonCopyLink } from 'common.components/lesson-copy-link/lesson-copy-link';
import { LessonThreeDots } from 'common.components/lesson-three-dots/lesson-three-dots';
import { getEndDate, getCalendarLessonStatus, getShouldLessonShowDeadline } from 'common.components/lesson/utils';
import LessonLikes from 'common.components/lessons/__lesson-likes/lessons__lesson-likes';
import { ClessonProgressData } from 'store/clesson-results/utils';
import { getCalendarLessonStatusKey, getLessonName, LessonPermissions } from 'utils/lesson';
import { b } from './cn';

import { CalendarLessonCardProgress } from './__progress/calendar-lesson-card__progress';

import './calendar-lesson-card.scss';

export interface Props {
    cardsCount: number;
    childLink: string;
    clesson: CLessonInCourse;
    courseId: number | null;
    onCloseLesson: SimpleCallback;
    onCopyLesson: SimpleCallback;
    onLessonClick: SimpleCallback;
    onLessonDelete: SimpleCallback;
    lessonPermissions: LessonPermissions;
    likesCount?: number;
    onShowLikesPopup: (clessonId: number, likeRef: Ref<HTMLElement>) => void;
    onHideLikesPopup: SimpleCallback;
    shouldShowSettings: boolean;
    shouldDisableSettings: boolean;
    shouldShowLikes: boolean;
    onSettingsClick?: SimpleCallback;
    setRef?: (ref: HTMLDivElement | null) => void;
    clessonProgress?: ClessonProgressData;
    clessonProgressLoadingStatus: LOADING_STATUSES;
}

// eslint-disable-next-line complexity
export const CalendarLessonCard = ({
    childLink,
    clesson,
    cardsCount,
    onLessonClick,
    onCloseLesson,
    onCopyLesson,
    onLessonDelete,
    courseId,
    shouldShowSettings,
    shouldDisableSettings,
    clessonProgress,
    clessonProgressLoadingStatus,
    lessonPermissions,
    shouldShowLikes,
    likesCount,
    onShowLikesPopup,
    onHideLikesPopup,
    onSettingsClick,
    setRef,
}: Props) => {
    const { id: clessonId, date_deadline: dateDeadline, status, lesson, mode } = clesson;

    const isControlWork = mode === CLESSON_MODES.CONTROL_WORK;

    const calendarLessonStatus = getCalendarLessonStatus(status);
    const shouldShowDeadline = getShouldLessonShowDeadline(calendarLessonStatus);
    const endDate = shouldShowDeadline ? getEndDate(dateDeadline) : null;

    let bottomContent;
    switch (calendarLessonStatus) {
        case LESSON_STATUS.ASSIGNED_IN_FUTURE:
        case LESSON_STATUS.ASSIGNED:
            bottomContent = <LessonCopyLink childLink={childLink} clessonId={clessonId} mix={b('copy-link')} />;
            break;
        case LESSON_STATUS.IN_PROGRESS:
        case LESSON_STATUS.FINISHED:
            bottomContent = (
                <CalendarLessonCardProgress
                    isControlWork={isControlWork}
                    progressData={clessonProgress}
                    progressLoadingStatus={clessonProgressLoadingStatus}
                    status={calendarLessonStatus}
                />
            );
            break;
        case LESSON_STATUS.NOT_ASSIGNED:
            bottomContent = (
                <Text
                    className={b('not-assigned-text')}
                    id="cardsCount"
                    typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphSRegular}
                    values={{ cardsCount }}
                />
            );
    }

    const shouldShowCalendarLikes = shouldShowLikes && !!likesCount;

    return (
        <div className={b()} ref={setRef} {...accessibleClick(onLessonClick)}>
            <div className={b('wrapper')}>
                <div className={b('content')}>
                    <div className={b('head')}>
                        <Text
                            id={getCalendarLessonStatusKey(calendarLessonStatus)}
                            typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphSRegular}
                        />
                        &nbsp;
                        {endDate && (
                            <Text
                                className={b('deadline')}
                                id="lesson-meta.period"
                                typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphSRegular}
                                values={{ type: 'openStart', endDate }}
                            />
                        )}
                        {shouldShowSettings && (
                            <div className={b('three-dots')}>
                                <LessonThreeDots
                                    childLink={childLink}
                                    clessonId={clessonId}
                                    courseId={courseId}
                                    disabled={shouldDisableSettings}
                                    lessonPermissions={lessonPermissions}
                                    onClick={onSettingsClick}
                                    onLessonClose={onCloseLesson}
                                    onLessonCopy={onCopyLesson}
                                    onLessonDelete={onLessonDelete}
                                />
                            </div>
                        )}
                    </div>
                    <div className={b('title')}>
                        <Text typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLMedium}>{getLessonName(lesson)}</Text>
                    </div>
                    <div className={b('bottom')}>{bottomContent}</div>
                </div>
                {shouldShowCalendarLikes && (
                    <div className={b('likes')}>
                        <LessonLikes
                            clessonId={clessonId}
                            likesCount={likesCount}
                            onMouseEvent={(target, enter) => {
                                if (enter) {
                                    onShowLikesPopup(clessonId, { current: target });
                                } else {
                                    onHideLikesPopup();
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
